import type { ReactNode } from "react";
import { useState } from "react";
import "./ChangePassword.scss";
import type { Password } from "../../types/password";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { PasswordNotification } from "./components/PasswordNotification";
import { GoogleAuthView } from "./components/GoogleAuthView";
import { StandardPasswordForm } from "./components/StandardPasswordForm";

interface ChangePasswordProps {
  password: Password;
  setPassword: React.Dispatch<React.SetStateAction<Password>>;
  updatePassword: (oldPassword: string, newPassword: string) => any;
}

/**
 * Main component for changing user passwords.
 * Handles both standard password changes and Google account unlinking flows.
 */
const ChangePassword = ({
  password,
  setPassword,
  updatePassword,
}: ChangePasswordProps) => {
  const getAuthMethod = useAuthStore((state) => state.getAuthMethod);
  const authMethod = getAuthMethod();
  const isGoogleAuth = authMethod === "google";
  const [showUnlinkForm, setShowUnlinkForm] = useState(false);

  const [error, setError] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [notification, setNotification] = useState<{
    title: string;
    message: ReactNode;
    color: string;
  } | null>(null);

  const handleError = (errorMessage: string) => {
    setNotification({
      title: "Error",
      message: errorMessage,
      color: "red",
    });
  };

  const handleSuccess = () => {
    // Form reset is handled by child components
  };

  // If user authenticated via Google, show unlink option
  if (isGoogleAuth) {
    return (
      <div className="change-password">
        {notification && (
          <PasswordNotification
            title={notification.title}
            message={notification.message}
            color={notification.color}
            onClose={() => setNotification(null)}
          />
        )}
        <GoogleAuthView
          showUnlinkForm={showUnlinkForm}
          setShowUnlinkForm={setShowUnlinkForm}
          password={password}
          setPassword={setPassword}
          onSuccess={handleSuccess}
          onError={handleError}
          setNotification={setNotification}
          setError={setError}
          error={error}
        />
      </div>
    );
  }

  return (
    <div className="change-password">
      {notification && (
        <PasswordNotification
          title={notification.title}
          message={notification.message}
          color={notification.color}
          onClose={() => setNotification(null)}
        />
      )}
      <StandardPasswordForm
        password={password}
        setPassword={setPassword}
        updatePassword={updatePassword}
        setNotification={setNotification}
        setError={setError}
        error={error}
      />
    </div>
  );
};

export default ChangePassword;
